import { ElementRef, Injectable } from '@angular/core';

export type Direction = 'top' | 'topright' | 'right' | 'bottomright' | 'bottom' | 'bottomleft' | 'left' | 'topleft';

/**
 * Contains methods that can control the scrolling of HTML elements.
 */
@Injectable()
export class ScrollService {

  constructor(private window: Window) {

  }

  /**
   * Scrolls the target into view of its most imeediate scrollable parent.
   * If target is a string, then it is considered an anchor.
   */
  scrollIntoView(target: ElementRef | string, hostElemRef?: ElementRef) {
    // need to wrap in setTimeout in case we have to get theScrollHost
    return setTimeout(() => {

      const elem: HTMLElement | undefined = (typeof target === 'string') ? this.findAnchorElem(target, hostElemRef) : target.nativeElement;
      const hostElem: HTMLElement | undefined = hostElemRef ? hostElemRef.nativeElement : this.findScrollHost(elem);

      if (!elem || !hostElem) {
        // no host then nothing to scroll
        return;
      }

      const hostOffset = (hostElem) ? { left: hostElem.offsetLeft, top: hostElem.offsetTop } : { left: 0, top: 0 };
      const runningOffset = { left: elem.offsetLeft, top: elem.offsetTop };


      let parentElemCur = elem.parentElement;
      let lastOffsetChild = elem;
      while (parentElemCur && hostElem !== parentElemCur) {
        if (parentElemCur === lastOffsetChild.offsetParent) {
          runningOffset.left += parentElemCur.offsetLeft;
          runningOffset.top += parentElemCur.offsetTop;
          lastOffsetChild = parentElemCur;
        }
        parentElemCur = parentElemCur.parentElement;
      }

      // // Useful console.log for debugging issues.   Don't delete.
      // console.log([
      //  'SCROLL',
      //  `Elem Offset: (${runningOffset.left}, ${runningOffset.top})`,
      //  `Host Offset: (${hostOffset.left}, ${hostOffset.top})`,
      //  `Calculated Offset: (${runningOffset.left - hostOffset.left}, ${runningOffset.top - hostOffset.top})`,
      //  elem,
      //  hostElem
      // ]);
      return this.scrollToPointHTML(hostElem, runningOffset.left - hostOffset.left, runningOffset.top - hostOffset.top);
    }, 0);
  }

  /**
   * scrolls to the farthest point of a specific dimension
   */
  scrollToDirection(hostElemRef: ElementRef, direction: Direction) {
    const elem: HTMLElement = hostElemRef.nativeElement;
    let point: { left: number, top: number };

    switch (direction) {
      case 'top': point = { left: elem.scrollLeft, top: 0 }; break;
      case 'topright': point = { left: elem.scrollWidth, top: 0 }; break;
      case 'right': point = { left: elem.scrollWidth, top: elem.scrollTop }; break;
      case 'bottomright': point = { left: elem.scrollWidth, top: elem.scrollHeight }; break;
      case 'bottom': point = { left: elem.scrollLeft, top: elem.scrollHeight }; break;
      case 'bottomleft': point = { left: 0, top: elem.scrollHeight }; break;
      case 'left': point = { left: 0, top: elem.scrollTop }; break;
      case 'topleft': point = { left: 0, top: 0 }; break;
    }

    if (!point) return undefined;

    return this.scrollToPointHTML(elem, point.left, point.top);
  }

  /**
   * scrolls a relative amount from existing scroll coordinates
   */
  scrollRelative(hostElemRef: ElementRef, relativeLeft: number, relativeTop: number) {
    const elem: HTMLElement = hostElemRef.nativeElement;
    const left = elem.scrollLeft + (relativeLeft || 0);
    const top = elem.scrollTop + (relativeTop || 0);
    return this.scrollToPointHTML(elem, left, top);
  }

  /**
   * scrolls to specific point within the host
   */
  scrollToPoint(hostElemRef: ElementRef, left: number, top: number) {
    return this.scrollToPointHTML(hostElemRef.nativeElement, left, top);
  }

  private findAnchorElem(anchor: string, hostElemRef?: ElementRef) {
    const hostElem: HTMLElement | undefined = hostElemRef ? hostElemRef.nativeElement : window.document.body;
    return hostElem?.querySelector(`#${anchor}`) || hostElem?.querySelector(`a[name=${anchor}]`);
  }
  /**
   * finds the first parent element that is capable of scrolling at this moment.
   * Must call setTimeout before calling this in order to get the most up to date ui info
   * @param childElem Element that is the child of the scroller
   */
  private findScrollHost(childElem: HTMLElement | undefined) {
    let parentElem = childElem?.parentElement;
    while (parentElem != null) {
      let overflow = this.getElementScrollCapability(parentElem);
      let scrollDims = { x: parentElem.scrollWidth, y: parentElem.scrollHeight };
      let clientDims = { x: parentElem.clientWidth, y: parentElem.clientHeight };
      if (parentElem === this.window.document.body) {
        // special exception for body and html element as their scroll is controlled by window.scrollTo
        const docElem = this.window.document.documentElement;
        const docOverflow = this.getElementScrollCapability(docElem);
        overflow = { x: overflow.x || docOverflow.x, y: overflow.y || docOverflow.y };
        scrollDims = { x: Math.max(scrollDims.x, docElem.scrollWidth), y: Math.max(scrollDims.y, docElem.scrollHeight) };
        clientDims = { x: Math.min(clientDims.x, docElem.clientWidth), y: Math.min(clientDims.y, docElem.clientHeight) };
      }

      if ((overflow.x && clientDims.x && clientDims.x < scrollDims.x) || (overflow.y && clientDims.y && clientDims.y < scrollDims.y)) {
        return parentElem;
      }
      parentElem = parentElem.parentElement;
    }
  }

  /**
   * returns true for x and y dimensions if scrolling is possible for the element
   */
  getElementScrollCapability(elem: HTMLElement) {
    const computedStyle = this.window.getComputedStyle(elem);

    return {
      x: ['auto', 'scroll'].indexOf(computedStyle.overflowX) !== -1,
      y: ['auto', 'scroll'].indexOf(computedStyle.overflowY) !== -1
    };
  }

  /**
   * this should be injected for the browser
   */
  private scrollToPointHTML(hostElem: HTMLElement, left: number, top: number) {
    return setTimeout(() => {
      if (hostElem) {
        if (hostElem === this.window.document.documentElement || hostElem === this.window.document.body) {
          this.window.scrollTo(left, top);
        }
        else {
          hostElem.scrollLeft = left;
          hostElem.scrollTop = top;
        }
      }
    }, 0);
  }
}
